import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, AlertTriangle, GripVertical, EyeOff, Eye } from 'lucide-react';
import {
  EncounterSectionTemplate,
  QuestionType,
  CreateTemplateRequest,
} from '../../types/encounterTemplate';
import {
  createCustomQuestion,
  deleteCustomQuestion,
  reorderQuestions,
  excludeStandardQuestion,
  includeStandardQuestion,
  getExcludedQuestions,
} from '../../utils/encounterTemplateApi';

interface TemplateEditorModalProps {
  practiceId: string;
  sectionName: string;
  templates: EncounterSectionTemplate[];
  onClose: () => void;
  onUpdate: () => void;
}

const TemplateEditorModal: React.FC<TemplateEditorModalProps> = ({
  practiceId,
  sectionName,
  templates,
  onClose,
  onUpdate,
}) => {
  const [localTemplates, setLocalTemplates] = useState<EncounterSectionTemplate[]>(templates);
  const [excludedTemplates, setExcludedTemplates] = useState<EncounterSectionTemplate[]>([]);
  const [showHiddenQuestions, setShowHiddenQuestions] = useState(false);
  const [isAddingQuestion, setIsAddingQuestion] = useState(false);
  const [newQuestion, setNewQuestion] = useState<Partial<CreateTemplateRequest>>({
    section_name: sectionName,
    question_key: '',
    question_text: '',
    question_type: 'text',
    is_required: false,
    options: [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [hideConfirm, setHideConfirm] = useState<string | null>(null);

  useEffect(() => {
    setLocalTemplates(templates);
  }, [templates]);

  // Fetch excluded questions when modal opens
  useEffect(() => {
    const fetchExcludedQuestions = async () => {
      try {
        const excluded = await getExcludedQuestions(practiceId, sectionName);
        setExcludedTemplates(excluded);
      } catch (err) {
        console.error('Failed to fetch excluded questions:', err);
      }
    };
    fetchExcludedQuestions();
  }, [practiceId, sectionName]);

  const handleAddQuestion = async () => {
    if (!newQuestion.question_text || !newQuestion.question_key) {
      setError('Question text and key are required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await createCustomQuestion(practiceId, sectionName, newQuestion as CreateTemplateRequest);
      setIsAddingQuestion(false);
      setNewQuestion({
        section_name: sectionName,
        question_key: '',
        question_text: '',
        question_type: 'text',
        is_required: false,
        options: [],
      });
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to add question');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteQuestion = async (templateId: string) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await deleteCustomQuestion(templateId);
      setDeleteConfirm(null);
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to delete question');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleHideQuestion = async (templateId: string) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await excludeStandardQuestion(practiceId, sectionName, templateId);
      setHideConfirm(null);
      // Refresh excluded questions list
      const excluded = await getExcludedQuestions(practiceId, sectionName);
      setExcludedTemplates(excluded);
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to hide question');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShowQuestion = async (templateId: string) => {
    setIsSubmitting(true);
    setError(null);

    try {
      await includeStandardQuestion(practiceId, sectionName, templateId);
      // Refresh excluded questions list
      const excluded = await getExcludedQuestions(practiceId, sectionName);
      setExcludedTemplates(excluded);
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to show question');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMoveUp = async (index: number) => {
    if (index === 0) return;

    const newOrder = [...localTemplates];
    [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    setLocalTemplates(newOrder);

    try {
      await reorderQuestions(
        practiceId,
        sectionName,
        newOrder.map((t) => t.id)
      );
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to reorder questions');
      setLocalTemplates(templates);
    }
  };

  const handleMoveDown = async (index: number) => {
    if (index === localTemplates.length - 1) return;

    const newOrder = [...localTemplates];
    [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    setLocalTemplates(newOrder);

    try {
      await reorderQuestions(
        practiceId,
        sectionName,
        newOrder.map((t) => t.id)
      );
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to reorder questions');
      setLocalTemplates(templates);
    }
  };

  const generateQuestionKey = (text: string): string => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-semibold text-gray-900">Edit Template</h2>
            <p className="text-sm text-gray-600 mt-1">
              Customize questions for the {sectionName} section
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Warning Banner */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 m-6 mb-0">
          <div className="flex">
            <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div className="ml-3">
              <p className="text-sm text-yellow-700 font-medium">
                Changes affect all encounters for your practice
              </p>
              <p className="text-sm text-yellow-600 mt-1">
                Adding or deleting questions will update the template for all future and existing encounters.
              </p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4 m-6 mb-0">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Questions List */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-3">
            {localTemplates.map((template, index) => (
              <div
                key={template.id}
                className="bg-gray-50 rounded-lg p-4 flex items-start justify-between border border-gray-200"
              >
                <div className="flex items-start space-x-3 flex-1">
                  <GripVertical className="w-5 h-5 text-gray-400 mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="text-sm font-medium text-gray-900">
                        {template.question_text}
                      </h4>
                      {template.is_standard && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          Standard
                        </span>
                      )}
                      {template.is_required && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          Required
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Type: {template.question_type.replace('_', ' ')} • Key: {template.question_key}
                    </p>
                    {template.options && template.options.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">
                        Options: {template.options.join(', ')}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center space-x-2 ml-4">
                  <button
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0 || isSubmitting}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move up"
                  >
                    ↑
                  </button>
                  <button
                    onClick={() => handleMoveDown(index)}
                    disabled={index === localTemplates.length - 1 || isSubmitting}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Move down"
                  >
                    ↓
                  </button>
                  {template.is_standard ? (
                    <button
                      onClick={() => setHideConfirm(template.id)}
                      disabled={isSubmitting}
                      className="text-gray-400 hover:text-orange-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Hide standard question"
                    >
                      <EyeOff className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={() => setDeleteConfirm(template.id)}
                      disabled={isSubmitting}
                      className="text-red-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      title="Delete question"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Add Question Form */}
          {isAddingQuestion ? (
            <div className="mt-4 bg-white border-2 border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-900 mb-3">Add Custom Question</h4>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Question Text *
                  </label>
                  <input
                    type="text"
                    value={newQuestion.question_text}
                    onChange={(e) => {
                      const text = e.target.value;
                      setNewQuestion({
                        ...newQuestion,
                        question_text: text,
                        question_key: generateQuestionKey(text),
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Allergies"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Question Key (auto-generated)
                  </label>
                  <input
                    type="text"
                    value={newQuestion.question_key}
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, question_key: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50"
                    placeholder="e.g., allergies"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Question Type
                  </label>
                  <select
                    value={newQuestion.question_type}
                    onChange={(e) => {
                      const newType = e.target.value as QuestionType;
                      // Auto-add one empty option when switching to select/multi_select
                      const needsOptions = newType === 'select' || newType === 'multi_select';
                      const hasOptions = newQuestion.options && newQuestion.options.length > 0;
                      setNewQuestion({
                        ...newQuestion,
                        question_type: newType,
                        options: needsOptions && !hasOptions ? [''] : newQuestion.options,
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="text">Text</option>
                    <option value="yes_no">Yes/No</option>
                    <option value="select">Select (dropdown)</option>
                    <option value="multi_select">Multi-select</option>
                    <option value="date">Date</option>
                    <option value="number">Number</option>
                  </select>
                </div>

                {(newQuestion.question_type === 'select' ||
                  newQuestion.question_type === 'multi_select') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Options
                    </label>
                    <div className="space-y-2">
                      {(newQuestion.options || []).map((option, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={option}
                            onChange={(e) => {
                              const newOptions = [...(newQuestion.options || [])];
                              newOptions[index] = e.target.value;
                              setNewQuestion({ ...newQuestion, options: newOptions });
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder={`Option ${index + 1}`}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newOptions = (newQuestion.options || []).filter((_, i) => i !== index);
                              setNewQuestion({ ...newQuestion, options: newOptions });
                            }}
                            className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            title="Remove option"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => {
                          const newOptions = [...(newQuestion.options || []), ''];
                          setNewQuestion({ ...newQuestion, options: newOptions });
                        }}
                        className="flex items-center space-x-1 text-blue-600 hover:text-blue-800 text-sm font-medium py-1"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Add option</span>
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_required"
                    checked={newQuestion.is_required}
                    onChange={(e) =>
                      setNewQuestion({ ...newQuestion, is_required: e.target.checked })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_required" className="ml-2 block text-sm text-gray-700">
                    Required field
                  </label>
                </div>
              </div>

              <div className="flex space-x-2 mt-4">
                <button
                  onClick={handleAddQuestion}
                  disabled={isSubmitting || !newQuestion.question_text || !newQuestion.question_key}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                >
                  {isSubmitting ? 'Adding...' : 'Add Question'}
                </button>
                <button
                  onClick={() => {
                    setIsAddingQuestion(false);
                    setNewQuestion({
                      section_name: sectionName,
                      question_key: '',
                      question_text: '',
                      question_type: 'text',
                      is_required: false,
                      options: [],
                    });
                  }}
                  disabled={isSubmitting}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setIsAddingQuestion(true)}
              className="mt-4 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors flex items-center justify-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span className="font-medium">Add Custom Question</span>
            </button>
          )}

          {/* Hidden Questions Section */}
          {excludedTemplates.length > 0 && (
            <div className="mt-6 border-t border-gray-200 pt-4">
              <button
                onClick={() => setShowHiddenQuestions(!showHiddenQuestions)}
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 text-sm font-medium"
              >
                <EyeOff className="w-4 h-4" />
                <span>
                  {showHiddenQuestions ? 'Hide' : 'Show'} hidden questions ({excludedTemplates.length})
                </span>
              </button>

              {showHiddenQuestions && (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-gray-500 mb-2">
                    These standard questions are hidden for your practice. Click the eye icon to restore them.
                  </p>
                  {excludedTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="bg-gray-100 rounded-lg p-3 flex items-center justify-between border border-gray-200 opacity-75"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h4 className="text-sm font-medium text-gray-600 line-through">
                            {template.question_text}
                          </h4>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-200 text-gray-600">
                            Hidden
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          Type: {template.question_type.replace('_', ' ')} • Key: {template.question_key}
                        </p>
                      </div>
                      <button
                        onClick={() => handleShowQuestion(template.id)}
                        disabled={isSubmitting}
                        className="text-green-500 hover:text-green-700 disabled:opacity-30 disabled:cursor-not-allowed ml-4"
                        title="Restore question"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-gray-900 text-white rounded-md hover:bg-gray-800 font-medium"
          >
            Done
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Question?</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will remove the question from all encounters. Existing encounter data will not be
              deleted.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => handleDeleteQuestion(deleteConfirm)}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => setDeleteConfirm(null)}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hide Confirmation Modal */}
      {hideConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Hide Standard Question?</h3>
            <p className="text-sm text-gray-600 mb-4">
              This will hide the standard question for your practice only. Other practices will not be
              affected. You can restore hidden questions at any time.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => handleHideQuestion(hideConfirm)}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
              >
                {isSubmitting ? 'Hiding...' : 'Hide Question'}
              </button>
              <button
                onClick={() => setHideConfirm(null)}
                disabled={isSubmitting}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateEditorModal;

